import { apiClient } from './api';

export interface TemplateInfo {
  id: string;
  name: string;
  description: string;
  category: string;
  agents_count: number;
  workflows_count: number;
  tags: string[];
  author: string;
  version: string;
  created_at: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimated_setup_time: string;
}

export interface TemplateListResponse {
  templates: TemplateInfo[];
  total: number;
  categories: string[];
}

export interface TemplatesByCategoryResponse {
  categories: Record<string, TemplateInfo[]>;
  total: number;
}

export interface ApplyTemplateRequest {
  template_id: string;
  workspace_id: string;
  force?: boolean;
}

export interface ApplyTemplateResponse {
  success: boolean;
  message: string;
  agents_created?: number;
  workflows_created?: number;
  template_info?: any;
  created_agents?: Array<{ id: number; name: string }>;
  created_workflows?: Array<{ id: number; name: string }>;
  conflicts?: string[];
  errors?: string[];
}

export interface ValidateTemplateRequest {
  template_id: string;
  workspace_id: string;
}

export interface ValidateTemplateResponse {
  valid: boolean;
  conflicts: string[];
  prerequisites_errors: string[];
  can_apply: boolean;
}

class TemplatesService {
  /**
   * List all available templates
   */
  async listTemplates(
    category?: string,
    search?: string,
    difficulty?: string
  ): Promise<TemplateListResponse> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    if (difficulty) params.append('difficulty', difficulty);

    const url = `/templates${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get<TemplateListResponse>(url);
    return response.data;
  }

  /**
   * List templates grouped by category
   */
  async listTemplatesByCategory(): Promise<TemplatesByCategoryResponse> {
    const response = await apiClient.get<TemplatesByCategoryResponse>('/templates/by-category');
    return response.data;
  }

  /**
   * Get detailed information about a specific template
   */
  async getTemplate(templateId: string): Promise<TemplateInfo> {
    const response = await apiClient.get<TemplateInfo>(`/templates/${templateId}`);
    return response.data;
  }

  /**
   * Validate if a template can be applied to a workspace
   */
  async validateTemplate(request: ValidateTemplateRequest): Promise<ValidateTemplateResponse> {
    const response = await apiClient.post<ValidateTemplateResponse>('/templates/validate', request);
    return response.data;
  }

  /**
   * Apply a template to a workspace
   */
  async applyTemplate(request: ApplyTemplateRequest): Promise<ApplyTemplateResponse> {
    const response = await apiClient.post<ApplyTemplateResponse>('/templates/apply', request);
    return response.data;
  }

  /**
   * Search templates
   */
  async searchTemplates(query: string): Promise<TemplateInfo[]> {
    const response = await this.listTemplates(undefined, query);
    return response.templates;
  }

  /**
   * Get templates by difficulty level
   */
  async getTemplatesByDifficulty(difficulty: string): Promise<TemplateInfo[]> {
    const response = await this.listTemplates(undefined, undefined, difficulty);
    return response.templates;
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category: string): Promise<TemplateInfo[]> {
    const response = await this.listTemplates(category);
    return response.templates;
  }
}

export const templatesService = new TemplatesService(); 